import Dynamic from '@base/Dynamic/Dynamic';
import styled from '@emotion/styled';
import { useLocalLoginInfo } from './Context/LoginLocalInfo';
import useLoginStepFlags from './utils/hooks/useLoginStepFlags';

const EnterNumberStep = Dynamic(() => import('./EnterNumberStep'));
const EnterEmailStep = Dynamic(() => import('./EnterEmailStep'));
const EnterEmailOtpStep = Dynamic(() => import('./EnterEmailOtpStep'));
const EnterOtpStep = Dynamic(() => import('./EnterOtpStep'));
const EnterPasswordStep = Dynamic(() => import('./EnterPasswordStep'));
const ConfirmAgeStep = Dynamic(() => import('./ConfirmAgeStep'));
const EnterNumberOtpStep = Dynamic(() => import('./EnterNumberOtpStep'));
const NewEnterEmailOTPStep = Dynamic(() => import('./NewEnterEmailOTPStep'));
const UpdateDetailsStep = Dynamic(() => import('./UpdateDetailsStep'));
const RegisterWithEmailOutsideIndia = Dynamic(() =>
  import('./RegisterWithEmailOutsideIndia')
);

const Container = styled.div`
  background: ${({ $primeTheme }) => ($primeTheme ? 'transparent' : 'white')};
  min-width: 480px;
  padding: 40px;
  border-radius: 16px;
  border: ${({ $primeTheme }) =>
    $primeTheme ? 'none' : '1px solid var(--color-divider)'};

  ${({ $primeTheme }) =>
    $primeTheme
      ? `
    --color-base-1: #252038;
    --color-divider: rgba(255, 255, 255, 0.72);
    --color-divider-tertiary: rgba(255, 255, 255, 0.72);
    --color-i-green: #B9A5FF;
    --color-i-green-focus: #9875FF;
    --color-i-white: #9875FF;
    --color-shadow: rgba(255, 255, 255, 0.24);
    --color-text-primary: #ffffff;
    --color-text-primary-light: rgba(255, 255, 255, 0.72);
    --color-text-secondary: rgba(255, 255, 255, 0.72);

    button {
      background-color: #ffffff;
      color: #9875FF;
    }

    input {
      color: #ffffff;
    }
  `
      : ''}

  @media (max-width: 480px) {
    min-width: 320px;
    max-width: 480px;
    padding: 20px;
    border-radius: 12px;
    border: 1px solid var(--color-divider)
`;

const PrimeThemeScope = styled.div`
  ${({ $primeTheme }) =>
    $primeTheme
      ? `
    --color-base-0: #120D23;
    --color-base-1: #252038;
    --color-divider: rgba(255, 255, 255, 0.72);
    --color-divider-tertiary: rgba(255, 255, 255, 0.72);
    --color-i-green: #B9A5FF;
    --color-i-green-focus: #9875FF;
    --color-i-white: #9875FF;
    --color-shadow: rgba(255, 255, 255, 0.24);
    --color-text-primary: #ffffff;
    --color-text-primary-light: rgba(255, 255, 255, 0.72);
    --color-text-secondary: rgba(255, 255, 255, 0.72);
    color: #ffffff;

    input {
      color: #ffffff;
    }
  `
      : ''}
`;

const LoginUI = () => {
  const {
    showEmailStep,
    showOutsideIndiaRegistrationProcess,
    showFirstStep,
    showAgeStep,
    showPhoneOTPStep,
    showEmailOTPStep,
    showEnterPasswordStep,
    showUpdateDetailsStep
  } = useLoginStepFlags();
  const { IsNewLoginModal, country, primeTheme } = useLocalLoginInfo();
  const isOutsideIndia = country ? country?.code !== 'IN' : false;
  const { countries, isTTU } = useLocalLoginInfo();

  const renderFirstStepForModal = () => {
    if (showEmailStep) return <NewEnterEmailOTPStep />;
    return <EnterNumberOtpStep />;
  };

  if (!isOutsideIndia && IsNewLoginModal && !isTTU) {
    return (
      <PrimeThemeScope $primeTheme={primeTheme}>
        {(showFirstStep ||
          ((showPhoneOTPStep || showEmailOTPStep) && !showUpdateDetailsStep)) &&
          renderFirstStepForModal()}
        {showUpdateDetailsStep && <UpdateDetailsStep />}
      </PrimeThemeScope>
    );
  }

  const renderFirstStep = () => {
    if (showEmailStep) return <EnterEmailStep />;
    return <EnterNumberStep />;
  };

  if (!countries.length) return null;
  if (showOutsideIndiaRegistrationProcess)
    return <RegisterWithEmailOutsideIndia />;
  return (
    <Container $primeTheme={primeTheme}>
      {showFirstStep && renderFirstStep()}
      {showAgeStep && <ConfirmAgeStep />}
      {showPhoneOTPStep && <EnterOtpStep />}
      {showEmailOTPStep && <EnterEmailOtpStep />}
      {showEnterPasswordStep && <EnterPasswordStep />}
    </Container>
  );
};

export default LoginUI;
